import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonIcon,
  IonSpinner,
  IonToast,
  IonItem,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mailOutline, lockClosedOutline, personOutline } from 'ionicons/icons';
import { useAuth } from '../contexts/AuthContext';
import ManageTixLogo from '../components/ManageTixLogo';

const Signup: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { signup, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [validationError, setValidationError] = useState('');

  const validateForm = () => {
    if (!email || !password || !confirmPassword) {
      setValidationError(t('errors.validation'));
      return false;
    }

    if (password.length < 6) {
      setValidationError(t('auth.passwordTooShort'));
      return false;
    }

    if (password !== confirmPassword) {
      setValidationError(t('auth.passwordsDoNotMatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setShowToast(true);
      return;
    }

    setLoading(true);

    try {
      await signup(email, password);
      history.push('/dashboard');
    } catch (error) {
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent
        fullscreen
        className="bg-gradient-to-br from-primary-600 to-purple-700"
      >
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
          <div className="max-w-md w-full">
            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-2xl rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-500 to-primary-700 text-white px-8 py-6">
                <div className="flex justify-center mb-4">
                  <ManageTixLogo size={64} showText={false} />
                </div>
                <h1 className="text-3xl font-bold text-center mb-2">
                  ManageTix
                </h1>
                <p className="text-lg text-center opacity-90">
                  {t('auth.createAccount')}
                </p>
              </div>

              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div className="relative group">
                      <IonItem
                        lines="none"
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 hover:border-primary-400 focus-within:border-primary-500 focus-within:shadow-lg"
                      >
                        <IonIcon
                          icon={mailOutline}
                          slot="start"
                          className="text-gray-500 dark:text-gray-400 mr-3"
                        />
                        <IonInput
                          type="email"
                          value={email}
                          onIonChange={(e) => setEmail(e.detail.value!)}
                          placeholder={t('auth.email')}
                          required
                          autocomplete="email"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      </IonItem>
                    </div>

                    <div className="relative group">
                      <IonItem
                        lines="none"
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 hover:border-primary-400 focus-within:border-primary-500 focus-within:shadow-lg"
                      >
                        <IonIcon
                          icon={lockClosedOutline}
                          slot="start"
                          className="text-gray-500 dark:text-gray-400 mr-3"
                        />
                        <IonInput
                          type="password"
                          value={password}
                          onIonChange={(e) => setPassword(e.detail.value!)}
                          placeholder={t('auth.password')}
                          required
                          autocomplete="new-password"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      </IonItem>
                    </div>

                    <div className="relative group">
                      <IonItem
                        lines="none"
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 hover:border-primary-400 focus-within:border-primary-500 focus-within:shadow-lg"
                      >
                        <IonIcon
                          icon={lockClosedOutline}
                          slot="start"
                          className="text-gray-500 dark:text-gray-400 mr-3"
                        />
                        <IonInput
                          type="password"
                          value={confirmPassword}
                          onIonChange={(e) =>
                            setConfirmPassword(e.detail.value!)
                          }
                          placeholder={t('auth.confirmPassword')}
                          required
                          autocomplete="new-password"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      </IonItem>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <IonButton
                      expand="block"
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transform transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <IonSpinner name="crescent" className="w-5 h-5" />
                      ) : (
                        t('auth.signup')
                      )}
                    </IonButton>
                  </div>

                  <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                    {t('auth.haveAccount')}{' '}
                    <a
                      href="/login"
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                      {t('auth.login')}
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => {
            setShowToast(false);
            clearError();
            setValidationError('');
          }}
          message={validationError || error || t('auth.signupError')}
          duration={3000}
          color="danger"
          position="top"
        />
      </IonContent>
    </IonPage>
  );
};

export default Signup;
